// ✅ src/components/admin/AdminNotificationsScreen.js
// ✅ Admin send notifications
// ✅ Manual UID fallback (works even if /users read is blocked by rules)
// ✅ Search tries: displayNameLower/emailLower -> fallback displayName/email (case-sensitive) -> phone

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { auth, db } from "../../lib/firebase";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  limit,
  query,
  serverTimestamp,
  where,
  writeBatch,
} from "firebase/firestore";

function safe(v) {
  return String(v ?? "").trim();
}
function lower(v) {
  return safe(v).toLowerCase();
}
function isLikelyEmail(s) {
  const t = lower(s);
  return t.includes("@") && t.includes(".");
}
function isLikelyPhone(s) {
  const t = safe(s);
  const digits = t.replace(/[^\d+]/g, "");
  return digits.replace(/\D/g, "").length >= 5;
}

export default function AdminNotificationsScreen({ goBack, role }) {
  // ✅ normalize role
  const roleNorm = safe(role).toUpperCase();
  const isAllowed = roleNorm === "ADMIN" || roleNorm === "WORKER";
  const roleLoaded = !!safe(role);

  const [mode, setMode] = useState("USER"); // USER | ALL

  // search
  const [userQuery, setUserQuery] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [selectedUid, setSelectedUid] = useState("");

  // manual uid fallback
  const [manualUid, setManualUid] = useState("");

  // compose
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [type, setType] = useState("ADMIN_MESSAGE");

  // state
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  // ✅ which uid to send to
  const toUid = useMemo(() => {
    return safe(selectedUid) || safe(manualUid);
  }, [selectedUid, manualUid]);

  const canSend = useMemo(() => {
    if (!roleLoaded) return false;
    if (!isAllowed) return false;
    if (!safe(title) || !safe(message)) return false;
    if (mode === "USER" && !safe(toUid)) return false;
    return true;
  }, [roleLoaded, isAllowed, title, message, mode, toUid]);

  // ✅ Debounce timer ref
  const tRef = useRef(null);

  // ✅ runSearch wrapped in useCallback so Hooks deps are correct
  const runSearch = useCallback(async () => {
    const qText = safe(userQuery);
    const qLower = lower(qText);

    if (!roleLoaded || !isAllowed) {
      setUserResults([]);
      return;
    }

    // Only search in USER mode
    if (mode !== "USER") {
      setUserResults([]);
      return;
    }

    if (!qText || qText.length < 2) {
      setUserResults([]);
      return;
    }

    try {
      setErr("");

      const usersRef = collection(db, "users");
      const queriesToRun = [];

      // 1) Name lower (best)
      queriesToRun.push(
        query(
          usersRef,
          where("displayNameLower", ">=", qLower),
          where("displayNameLower", "<=", qLower + "\uf8ff"),
          limit(10)
        )
      );

      // 2) Email lower (best)
      queriesToRun.push(
        query(
          usersRef,
          where("emailLower", ">=", qLower),
          where("emailLower", "<=", qLower + "\uf8ff"),
          limit(10)
        )
      );

      // 3) Fallback displayName (case-sensitive)
      queriesToRun.push(
        query(
          usersRef,
          where("displayName", ">=", qText),
          where("displayName", "<=", qText + "\uf8ff"),
          limit(10)
        )
      );

      // 4) Fallback email (case-sensitive) only if it looks like email
      if (isLikelyEmail(qText)) {
        queriesToRun.push(
          query(
            usersRef,
            where("email", ">=", qText),
            where("email", "<=", qText + "\uf8ff"),
            limit(10)
          )
        );
      }

      // 5) Phone only if it looks like a phone (✅ now isLikelyPhone is used)
      if (isLikelyPhone(qText)) {
        queriesToRun.push(
          query(
            usersRef,
            where("phone", ">=", qText),
            where("phone", "<=", qText + "\uf8ff"),
            limit(10)
          )
        );
      }

      const results = await Promise.all(
        queriesToRun.map(async (qq) => {
          try {
            const snap = await getDocs(qq);
            return { snap, error: null };
          } catch (error) {
            return { snap: null, error };
          }
        })
      );

      // permission denied?
      const permErr = results.find((r) => {
        const m = String(r?.error?.message || "").toLowerCase();
        return m.includes("permission") || m.includes("missing or insufficient permissions");
      });

      if (permErr) {
        const m = permErr.error?.message || "permission-denied";
        setUserResults([]);
        setErr(
          m +
            " — Firestore rules block reading /users. Use Manual UID input or allow ADMIN/WORKER read access."
        );
        return;
      }

      // merge docs
      const all = [];
      for (const r of results) {
        if (r.snap) r.snap.docs.forEach((d) => all.push({ id: d.id, ...d.data() }));
      }

      // dedupe by id
      const map = new Map();
      for (const u of all) map.set(u.id, u);
      const list = Array.from(map.values()).slice(0, 12);

      setUserResults(list);

      // index errors?
      const nonPermErrors = results.filter((r) => r.error);
      if (nonPermErrors.length) {
        const mm = String(nonPermErrors[0]?.error?.message || "").toLowerCase();
        if (mm.includes("index")) {
          setErr(
            "Missing index: Firestore needs an index for one of these queries. Open the console error link and create the index."
          );
        }
      }
    } catch (e) {
      const m = e?.message || "Search failed";
      setErr(m);
      setUserResults([]);
    }
  }, [userQuery, roleLoaded, isAllowed, mode]);

  // ✅ debounce search
  useEffect(() => {
    if (tRef.current) clearTimeout(tRef.current);

    // Only debounce-search in USER mode
    if (mode !== "USER") return;

    tRef.current = setTimeout(() => {
      runSearch();
    }, 250);

    return () => {
      if (tRef.current) clearTimeout(tRef.current);
    };
    // ✅ runSearch contains the real deps (userQuery, roleLoaded, isAllowed, mode)
    // ✅ selection changes won't trigger this effect
  }, [mode, runSearch]);

  async function sendToUid(sendUid, payload) {
    const adminUid = auth.currentUser?.uid || "admin";

    await addDoc(collection(db, "notifications", sendUid, "items"), {
      title: safe(payload.title),
      message: safe(payload.message),
      type: safe(payload.type) || "ADMIN_MESSAGE",
      data: payload.data || { fromAdmin: true },
      read: false,
      createdAt: serverTimestamp(),
      createdBy: adminUid,
    });
  }

  async function broadcastToUsers(uids, payload) {
    const adminUid = auth.currentUser?.uid || "admin";

    const CHUNK = 400; // < 500 write limit
    for (let i = 0; i < uids.length; i += CHUNK) {
      const part = uids.slice(i, i + CHUNK);
      const batch = writeBatch(db);

      for (const uid of part) {
        const ref = doc(collection(db, "notifications", uid, "items"));
        batch.set(ref, {
          title: safe(payload.title),
          message: safe(payload.message),
          type: safe(payload.type) || "ADMIN_MESSAGE",
          data: payload.data || { fromAdmin: true },
          read: false,
          createdAt: serverTimestamp(),
          createdBy: adminUid,
        });
      }

      await batch.commit();
    }
  }

  async function sendNow() {
    if (!canSend) return;

    setSending(true);
    setMsg("");
    setErr("");

    try {
      const payload = { title, message, type, data: { fromAdmin: true } };

      if (mode === "USER") {
        const target = safe(toUid);
        await sendToUid(target, payload);
        setMsg(`✅ Sent to user: ${target}`);
      } else {
        const usersSnap = await getDocs(query(collection(db, "users"), limit(300)));
        const uids = usersSnap.docs.map((d) => d.id);

        if (!uids.length) setErr("No users found to broadcast.");
        else {
          await broadcastToUsers(uids, payload);
          setMsg(`✅ Broadcast sent to ${uids.length} users!`);
        }
      }

      setTitle("");
      setMessage("");
      setSelectedUid("");
      setManualUid("");
      setUserResults([]);
      setUserQuery("");
    } catch (e) {
      setErr(e?.message || "❌ Failed to send. Check rules / permissions.");
    } finally {
      setSending(false);
    }
  }

  if (!roleLoaded) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack} type="button">
            ←
          </button>
          <div>
            <div style={S.h1}>Notifications</div>
            <div style={S.sub}>Loading role...</div>
          </div>
        </div>
        <div style={S.card}>Loading...</div>
      </div>
    );
  }

  if (!isAllowed) {
    return (
      <div style={S.page}>
        <div style={S.top}>
          <button style={S.back} onClick={goBack} type="button">
            ←
          </button>
          <div>
            <div style={S.h1}>Notifications</div>
            <div style={S.sub}>Access denied</div>
          </div>
        </div>
        <div style={S.card}>
          You need <b>ADMIN</b> or <b>WORKER</b> role.
          <div style={{ marginTop: 8, fontWeight: 900, fontSize: 12, color: "rgba(15,23,42,0.65)" }}>
            Your role: <b>{roleNorm || "—"}</b>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.top}>
        <button style={S.back} onClick={goBack} type="button">
          ←
        </button>
        <div>
          <div style={S.h1}>Admin Notifications</div>
          <div style={S.sub}>Send to one user or broadcast to everyone.</div>
        </div>
        <div style={{ marginLeft: "auto" }}>
          <span style={S.badge}>{roleNorm}</span>
        </div>
      </div>

      <div style={S.wrap}>
        <div style={S.panel}>
          <div style={S.row}>
            <button
              style={{ ...S.tab, ...(mode === "USER" ? S.tabOn : null) }}
              onClick={() => setMode("USER")}
              type="button"
            >
              Single user
            </button>
            <button
              style={{ ...S.tab, ...(mode === "ALL" ? S.tabOn : null) }}
              onClick={() => setMode("ALL")}
              type="button"
            >
              Broadcast
            </button>
          </div>

          {mode === "USER" && (
            <div style={{ marginTop: 12 }}>
              <div style={S.label}>Find user (name / email / phone)</div>
              <input
                style={S.input}
                value={userQuery}
                onChange={(e) => setUserQuery(e.target.value)}
                placeholder="Type at least 2 letters..."
              />

              <div style={{ marginTop: 10 }}>
                <div style={S.label}>Manual UID (works even if search is blocked)</div>
                <input
                  style={S.input}
                  value={manualUid}
                  onChange={(e) => setManualUid(e.target.value)}
                  placeholder="Paste user UID here (optional)"
                />
                <div style={S.help}>
                  Tip: open a user doc id in <b>/users</b> collection — that document ID is the UID.
                </div>
              </div>

              {toUid ? (
                <div style={S.selected}>
                  Target UID: <b>{toUid}</b>{" "}
                  <button
                    style={S.smallBtn}
                    onClick={() => {
                      setSelectedUid("");
                      setManualUid("");
                    }}
                    type="button"
                  >
                    Clear
                  </button>
                </div>
              ) : null}

              {userResults.length > 0 && !selectedUid && (
                <div style={S.list}>
                  {userResults.map((u) => (
                    <button
                      key={u.id}
                      style={S.listItem}
                      onClick={() => setSelectedUid(u.id)}
                      title={u.id}
                      type="button"
                    >
                      <div style={S.listTitle}>{u.displayName || "No name"}</div>
                      <div style={S.listSub}>{u.email || u.phone || u.id}</div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 14 }}>
            <div style={S.label}>Type (optional)</div>
            <input
              style={S.input}
              value={type}
              onChange={(e) => setType(e.target.value)}
              placeholder="ADMIN_MESSAGE"
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={S.label}>Title</div>
            <input
              style={S.input}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="E.g. Maintenance notice"
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <div style={S.label}>Message</div>
            <textarea
              style={S.textarea}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Write the notification..."
            />
          </div>

          <div style={{ marginTop: 12, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
            <button
              style={{ ...S.primary, opacity: canSend ? 1 : 0.55 }}
              disabled={!canSend || sending}
              onClick={sendNow}
              type="button"
            >
              {sending ? "Sending..." : mode === "ALL" ? "Broadcast" : "Send"}
            </button>

            {msg ? <div style={S.msgOk}>{msg}</div> : null}
            {err ? <div style={S.msgErr}>{err}</div> : null}
          </div>
        </div>
      </div>
    </div>
  );
}

const S = {
  page: {
    minHeight: "100vh",
    padding: 14,
    background: "linear-gradient(180deg,#f7f8ff 0%,#eef2ff 45%,#f8fafc 100%)",
  },
  top: {
    maxWidth: 980,
    margin: "0 auto 14px",
    display: "flex",
    gap: 12,
    alignItems: "center",
  },
  back: {
    width: 42,
    height: 42,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
  },
  h1: { fontSize: 18, fontWeight: 950, color: "#0f172a" },
  sub: { marginTop: 2, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.60)" },
  badge: {
    display: "inline-block",
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.9)",
    fontSize: 12,
    fontWeight: 900,
    color: "#0f172a",
  },
  wrap: { maxWidth: 980, margin: "0 auto" },
  panel: {
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
    boxShadow: "0 14px 35px rgba(15, 23, 42, 0.06)",
    padding: 14,
  },
  row: { display: "flex", gap: 10 },
  tab: {
    padding: "10px 12px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.8)",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },
  tabOn: {
    background: "rgba(99,102,241,0.12)",
    border: "1px solid rgba(99,102,241,0.22)",
  },
  label: { fontSize: 12, fontWeight: 900, color: "rgba(15,23,42,0.70)" },
  help: { marginTop: 6, fontSize: 12, fontWeight: 800, color: "rgba(15,23,42,0.55)" },
  input: {
    marginTop: 6,
    width: "100%",
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 800,
    background: "white",
  },
  textarea: {
    marginTop: 6,
    width: "100%",
    minHeight: 110,
    padding: "10px 12px",
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.12)",
    outline: "none",
    fontWeight: 800,
    resize: "vertical",
    background: "white",
  },
  primary: {
    padding: "10px 14px",
    borderRadius: 14,
    border: "1px solid rgba(99,102,241,0.22)",
    background: "rgba(99,102,241,0.16)",
    cursor: "pointer",
    fontWeight: 950,
  },
  msgOk: { fontSize: 12, fontWeight: 900, color: "rgba(34,197,94,1)" },
  msgErr: { fontSize: 12, fontWeight: 900, color: "rgba(239,68,68,1)" },

  list: { marginTop: 10, display: "grid", gap: 8 },
  listItem: {
    textAlign: "left",
    padding: 12,
    borderRadius: 14,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(248,250,252,0.75)",
    cursor: "pointer",
  },
  listTitle: { fontWeight: 950, fontSize: 13, color: "#0f172a" },
  listSub: { marginTop: 2, fontWeight: 800, fontSize: 12, color: "rgba(15,23,42,0.65)" },

  selected: {
    marginTop: 10,
    padding: 10,
    borderRadius: 14,
    border: "1px dashed rgba(15,23,42,0.25)",
    background: "rgba(255,255,255,0.7)",
    fontWeight: 900,
    fontSize: 12,
  },
  smallBtn: {
    marginLeft: 8,
    padding: "6px 10px",
    borderRadius: 999,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "white",
    cursor: "pointer",
    fontWeight: 900,
    fontSize: 12,
  },
  card: {
    maxWidth: 980,
    margin: "0 auto",
    padding: 14,
    borderRadius: 18,
    border: "1px solid rgba(15,23,42,0.10)",
    background: "rgba(255,255,255,0.92)",
  },
};
